import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Linking, ScrollView, TextInput } from 'react-native'
import { useStore } from 'zens-store'
import Constants from 'expo-constants'

import { buildAbsoluteUrl } from './constants'
import { useChatUsage } from './use-chat-usage'
import type { Chat, ChatMessage, ReportPreview } from './types'

export const useChat = (companyId: number) => {
  const { t } = useTranslation()
  const store = useStore()
  const scrollViewRef = useRef<ScrollView>(null)
  const inputRef = useRef<TextInput>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputText, setInputText] = useState('')
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null)
  const [chats, setChats] = useState<Chat[]>([])
  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  const [isLoadingChats, setIsLoadingChats] = useState(true)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [inputHeight, setInputHeight] = useState(40)
  const [previewModalData, setPreviewModalData] = useState<ReportPreview | null>(null)
  const [hoveredPreviewAction, setHoveredPreviewAction] = useState<{ messageId: string; action: 'preview' | 'download' } | null>(null)
  const [hoveredMessageAction, setHoveredMessageAction] = useState<{ messageId: string; action: 'copy' } | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([])
  const [isDeletingChats, setIsDeletingChats] = useState(false)
  const [hoveredChatTitleId, setHoveredChatTitleId] = useState<string | null>(null)
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const chatMessagesRef = useRef<Map<string, ChatMessage[]>>(new Map())
  const currentChatIdRef = useRef<string | null>(null)
  const loadingChatIdRef = useRef<string | null>(null)

  const chatbotApiUrl = Constants.expoConfig?.extra?.chatbot?.apiUrl || ''
  const chatsBaseUrl = `${chatbotApiUrl}/api/v1/companies/${companyId}/chats`
  const { usage, usageError, refreshUsage, updateUsage } = useChatUsage(companyId, chatbotApiUrl, store.auth.token)
  const isCreditsExhausted = usage?.unlimited === false && usage.credits_remaining === 0
  const language = store.i18n.language === 'nl' ? 'nl' : 'en'

  // Keep refs in sync with state
  currentChatIdRef.current = currentChatId
  loadingChatIdRef.current = loadingChatId

  const isLoading = loadingChatId !== null && loadingChatId === currentChatId

  const syncMessagesFromCache = useCallback((chatId: string | null) => {
    if (!chatId) {
      setMessages([])
      return
    }
    const cached = chatMessagesRef.current.get(chatId)
    setMessages(cached ? [...cached] : [])
  }, [])

  const updateCacheAndMaybeSync = useCallback((chatId: string, updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    const current = chatMessagesRef.current.get(chatId) || []
    const updated = updater(current)
    chatMessagesRef.current.set(chatId, updated)
    // Only sync to visible state if this is the currently viewed chat
    if (currentChatIdRef.current === chatId) {
      setMessages([...updated])
    }
  }, [])

  const downloadPreview = useCallback(
    async ({ url, filename, messageId, chatId }: { url: string; filename?: string; messageId: string; chatId: string }) => {
      if (!chatbotApiUrl || !url) {
        return
      }

      const absoluteUrl = buildAbsoluteUrl(chatbotApiUrl, url)
      if (!absoluteUrl) {
        updateCacheAndMaybeSync(chatId, (prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  reportPreviewLoading: false,
                  reportPreviewError: t('Preview unavailable'),
                }
              : msg,
          ),
        )
        return
      }

      try {
        const response = await fetch(absoluteUrl)

        if (!response.ok) {
          throw new Error('Failed to fetch preview')
        }

        const data = await response.json()
        const preview: ReportPreview = {
          filename: filename || data.filename || 'preview.json',
          columns: Array.isArray(data.columns) ? data.columns : [],
          rows: Array.isArray(data.rows) ? data.rows : [],
          rowCount: typeof data.row_count === 'number' ? data.row_count : data.rows?.length ?? 0,
          previewRowCount:
            typeof data.preview_row_count === 'number' ? data.preview_row_count : data.rows?.length ?? 0,
        }

        updateCacheAndMaybeSync(chatId, (prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  reportPreview: preview,
                  reportPreviewLoading: false,
                  reportPreviewError: undefined,
                }
              : msg,
          ),
        )
      } catch (error) {
        console.error('Failed to fetch report preview', error)
        updateCacheAndMaybeSync(chatId, (prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  reportPreviewLoading: false,
                  reportPreviewError: t('Unable to load preview.'),
                }
              : msg,
          ),
        )
      }
    },
    [chatbotApiUrl, store.auth.token, t, updateCacheAndMaybeSync],
  )

  const copyMessageContent = useCallback(async (messageId: string, content: string) => {
    if (!content) return
    try {
      let copied = false

      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content)
        copied = true
      } else if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea')
        textarea.value = content
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        try {
          copied = document.execCommand('copy')
        } catch (error) {
          console.error('Fallback copy failed', error)
        } finally {
          document.body.removeChild(textarea)
        }
      }

      if (copied) {
        if (copyResetTimeout.current) {
          clearTimeout(copyResetTimeout.current)
        }
        setCopiedMessageId(messageId)
        copyResetTimeout.current = setTimeout(() => {
          setCopiedMessageId((current) => (current === messageId ? null : current))
        }, 2000)
      }
    } catch (error) {
      console.error('Failed to copy message:', error)
    }
  }, [])

  const handleReportDownload = useCallback(
    async (url?: string) => {
      if (!url) return

      const absoluteUrl = buildAbsoluteUrl(chatbotApiUrl, url)
      if (!absoluteUrl) return

      try {
        await Linking.openURL(absoluteUrl)
      } catch (error) {
        console.error('Failed to open report URL', error)
      }
    },
    [chatbotApiUrl],
  )

  const fetchChats = useCallback(async () => {
    if (!chatbotApiUrl) return
    try {
      setIsLoadingChats(true)
      const response = await fetch(chatsBaseUrl, {
        headers: {
          Authorization: `Bearer ${store.auth.token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setChats(
          data.chats.map((chat: any) => ({
            chatId: chat.chat_id,
            companyId: chat.company_id,
            title: chat.title || '',
            createdAt: new Date(chat.created_at),
            updatedAt: new Date(chat.updated_at),
            messageCount: chat.message_count,
          })),
        )
      }
    } catch (error) {
      console.error('Failed to fetch chats:', error)
    } finally {
      setIsLoadingChats(false)
    }
  }, [chatbotApiUrl, chatsBaseUrl, store.auth])

  useEffect(() => {
    setSelectedChatIds((prev) => prev.filter((id) => chats.some((chat) => chat.chatId === id)))
  }, [chats])

  const handleToggleChatSelection = useCallback((chatId: string) => {
    setSelectedChatIds((prev) => (prev.includes(chatId) ? prev.filter((id) => id !== chatId) : [...prev, chatId]))
  }, [])

  const handleSelectAllChats = useCallback(() => {
    setSelectedChatIds(chats.map((chat) => chat.chatId))
  }, [chats])

  const handleClearSelection = useCallback(() => {
    setSelectedChatIds([])
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (!chatbotApiUrl || selectedChatIds.length === 0) return

    const confirmationMessage = t('Delete {{count}} chats? This action cannot be undone.', {
      count: selectedChatIds.length,
    })
    const isConfirmed =
      typeof window === 'undefined' ? true : window.confirm ? window.confirm(confirmationMessage) : true
    if (!isConfirmed) return

    setIsDeletingChats(true)
    try {
      const response = await fetch(`${chatsBaseUrl}/soft-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${store.auth.token}`,
        },
        body: JSON.stringify({
          chat_ids: selectedChatIds,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to delete chats')
      }

      const deletedSet = new Set(selectedChatIds)
      // Clean up cache for deleted chats
      deletedSet.forEach((id) => chatMessagesRef.current.delete(id))
      setChats((prev) => prev.filter((chat) => !deletedSet.has(chat.chatId)))
      if (currentChatId && deletedSet.has(currentChatId)) {
        setCurrentChatId(null)
        setMessages([])
      }
      setSelectedChatIds([])
      fetchChats()
    } catch (error) {
      console.error('Failed to delete chats:', error)
    } finally {
      setIsDeletingChats(false)
    }
  }, [chatbotApiUrl, selectedChatIds, store.auth.token, currentChatId, fetchChats, t, chatsBaseUrl])

  const fetchChatHistory = useCallback(
    async (chatId: string) => {
      if (!chatbotApiUrl) return

      // Don't overwrite cache if there's an active SSE stream for this chat
      if (loadingChatIdRef.current === chatId) {
        syncMessagesFromCache(chatId)
        return
      }

      try {
        const response = await fetch(`${chatsBaseUrl}/${chatId}`, {
          headers: {
            Authorization: `Bearer ${store.auth.token}`,
          },
        })
        if (response.ok) {
          const data = await response.json()
          const mappedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
            id: msg.message_id,
            role: msg.role,
            type: msg.preview ? 'preview' : 'message',
            content: msg.message || '',
            status: 'done',
            createdAt: new Date(msg.created_at),
            reportStatus: msg.report ? 'ready' : msg.preview ? 'pending' : undefined,
            reportPreviewLoading: !!msg.preview,
            reportUrl: msg.report || undefined,
            reportFilename: msg.report ? msg.report.split('/').pop() : undefined,
          }))

          // Update cache and sync to visible state
          chatMessagesRef.current.set(chatId, mappedMessages)
          if (currentChatIdRef.current === chatId) {
            setMessages([...mappedMessages])
          }

          // Trigger preview downloads for messages with preview URLs
          data.messages.forEach((msg: any) => {
            if (msg.preview) {
              downloadPreview({
                url: msg.preview,
                filename: msg.preview.split('/').pop(),
                messageId: msg.message_id,
                chatId,
              })
            }
          })
        }
      } catch (error) {
        console.error('Failed to fetch chat history:', error)
      }
    },
    [chatbotApiUrl, store.auth, downloadPreview, syncMessagesFromCache, chatsBaseUrl],
  )

  const createNewChat = useCallback(async (title: string) => {
    if (!chatbotApiUrl) return null
    try {
      const response = await fetch(chatsBaseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${store.auth.token}`,
        },
        body: JSON.stringify({
          title,
        }),
      })
      if (response.ok) {
        const data = await response.json()
        
        const newChat: Chat = {
          chatId: data.chat_id,
          companyId: data.company_id,
          title: data.title || title || '',
          createdAt: new Date(data.created_at),
          updatedAt: new Date(data.created_at),
          messageCount: 0,
        }
        
        setChats((prev) => [newChat, ...prev])
        currentChatIdRef.current = data.chat_id
        setCurrentChatId(data.chat_id)
        chatMessagesRef.current.set(data.chat_id, [])
        setMessages([])
        
        return newChat
      }
    } catch (error) {
      console.error('Failed to create chat:', error)
    }
    return null
  }, [chatbotApiUrl, chatsBaseUrl, store.auth])

  const handleSSEEvent = useCallback((data: any, messageId: string, targetChatId: string) => {
    if (data.type === 'usage') {
      updateUsage(data.content)
      return
    }
    updateCacheAndMaybeSync(targetChatId, (prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) {
          return msg
        } 

        switch (data.type) {
          case 'status':
            if (data.content === 'Initializing') {
              console.log('Status update:', data.content)
            }
            
            if (data.content === 'Thinking') {
              console.log('Status update:', data.content)
            }

            return { ...msg, statusMessage: data.content }
          case 'message':
            return {
              ...msg,
              content: data.content,
              status: data.clarification_needed ? 'done' : msg.status,
            }
          case 'report':
            return {
              ...msg,
              reportStatus: 'ready',
              reportUrl: data.content?.url,
              reportFilename: data.content?.filename,
            }
          case 'preview': {
            const url = data.content?.url
            const filename = data.content?.filename
            
            if (url) {
              downloadPreview({ url, filename, messageId, chatId: targetChatId })
              return {
                ...msg,
                reportStatus: 'pending',
                reportPreviewLoading: true,
                reportPreviewError: undefined,
              }
            }
            
            return {
              ...msg,
              reportStatus: 'pending',
              reportPreviewLoading: false,
              reportPreviewError: t('Preview unavailable'),
            }
          }
          case 'error':
            return {
              ...msg,
              content: data.content || msg.content,
              status: 'error',
            }
          case 'done':
            return { ...msg, status: 'done' }
          default:
            return msg
        }
      }),
    )
  }, [downloadPreview, t, updateCacheAndMaybeSync, updateUsage])

  const sendMessage = useCallback(
    async (message: string) => {
      const trimmedMessage = message.trim()
      if (!chatbotApiUrl || !trimmedMessage || isCreditsExhausted) return

      let chatId = currentChatId
      if (!chatId) {
        const chatTitle = trimmedMessage
        const newChat = await createNewChat(chatTitle)
        if (!newChat) return
        chatId = newChat.chatId
      }

      const targetChatId = chatId

      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        type: 'message',
        content: message,
        status: 'sending',
        createdAt: new Date(),
      }

      const assistantMessage: ChatMessage = {
        id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        type: 'message',
        content: '',
        status: 'thinking',
        createdAt: new Date(),
      }

      // Write to cache and sync to visible state
      updateCacheAndMaybeSync(targetChatId, (prev) => [...prev, userMessage, assistantMessage])
      setInputText('')
      setInputHeight(40)
      setLoadingChatId(targetChatId)

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      let responseError: string | null = null
      try {
        const token = store.auth.token
        const response = await fetch(
          `${chatsBaseUrl}/${targetChatId}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream',
              'Accept-Language': language,
            },
            body: JSON.stringify({ message }),
            signal: abortController.signal,
          },
        )

        if (!response.ok) {
          const payload = await response.json().catch(() => null)
          const detail = payload?.detail

          if (detail?.code === 'CHATBOT_CREDITS_EXHAUSTED') {
            updateUsage(detail)
            // The server rejected the request before generation; show the independent notice.
            updateCacheAndMaybeSync(targetChatId, (prev) =>
              prev.filter((msg) => msg.id !== userMessage.id && msg.id !== assistantMessage.id),
            )

            if (currentChatIdRef.current === targetChatId) {
              setInputText(message)
            } 
            
            return
          } else if (detail?.code === 'CHATBOT_REQUEST_IN_PROGRESS') {
            responseError = t('Another request is in progress for your company. Please try again shortly.')
          }

          throw new Error('Failed to send message')
        }

        const reader = response.body?.getReader()
        const decoder = new TextDecoder()

        if (!reader) {
          throw new Error('No response body')
        }

        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6))
                handleSSEEvent(data, assistantMessage.id, targetChatId)
              } catch (e) {
                console.error('Failed to parse SSE data:', e)
              }
            }
          }
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        
        console.error('Failed to send message:', error)
        updateCacheAndMaybeSync(targetChatId, (prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, content: responseError || t('Failed to get response. Please try again.'), status: 'error' }
              : msg,
          ),
        )
      } finally {
        abortControllerRef.current = null
        setLoadingChatId(null)
        fetchChats()
        refreshUsage()
      }
    },
    [chatbotApiUrl, companyId, currentChatId, createNewChat, store.auth, t, fetchChats, handleSSEEvent, updateCacheAndMaybeSync, language, refreshUsage, updateUsage, isCreditsExhausted],
  )

  const cancelRequest = useCallback(() => {
    const targetChatId = loadingChatIdRef.current
    abortControllerRef.current?.abort()
    abortControllerRef.current = null

    if (targetChatId) {
      const cached = chatMessagesRef.current.get(targetChatId) || []
      const lastUserIndex = cached.map((m) => m.role).lastIndexOf('user')
      
      if (lastUserIndex !== -1) {
        const lastUserMessage = cached[lastUserIndex]
        setInputText(lastUserMessage.content)
        const restored = cached.filter((_msg, index) => index < lastUserIndex)
        chatMessagesRef.current.set(targetChatId, restored)
        
        if (currentChatIdRef.current === targetChatId) {
          setMessages([...restored])
        }
      }
    }

    setLoadingChatId(null)
  }, [])

  const handleSelectChat = useCallback(
    (chatId: string) => {
      // Save current messages to cache before switching
      const prevChatId = currentChatIdRef.current
      
      if (prevChatId) {
        // Messages state is already in sync with cache via updateCacheAndMaybeSync,
        // but save a snapshot in case of any direct setMessages calls
        chatMessagesRef.current.set(prevChatId, messages)
      }

      setCurrentChatId(chatId)

      // If there's an active SSE stream for this chat, restore from cache
      if (loadingChatIdRef.current === chatId) {
        syncMessagesFromCache(chatId)
      } else {
        // Check if we have cached messages for this chat
        const cached = chatMessagesRef.current.get(chatId)
        
        if (cached && cached.length > 0) {
          setMessages([...cached])
        } else {
          fetchChatHistory(chatId)
        }
      }

      setTimeout(() => inputRef.current?.focus(), 0)
    },
    [fetchChatHistory, messages, syncMessagesFromCache],
  )

  const handleNewChat = useCallback(() => {
    // Save current messages to cache before switching
    const prevChatId = currentChatIdRef.current
    if (prevChatId) {
      chatMessagesRef.current.set(prevChatId, messages)
    }

    setCurrentChatId(null)
    setMessages([])
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [messages])

  const handleKeyPress = useCallback(
    (e: any) => {
      if (e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey) {
        e.preventDefault()
        sendMessage(inputText)
      }
    },
    [inputText, sendMessage],
  )

  useEffect(() => {
    fetchChats()
  }, [fetchChats])

  useEffect(() => {
    scrollViewRef.current?.scrollToEnd({ animated: true })
  }, [messages, isCreditsExhausted])

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close()
      abortControllerRef.current?.abort()
      
      if (copyResetTimeout.current) {
        clearTimeout(copyResetTimeout.current)
      }
    }
  }, [])

  return {
    scrollViewRef,
    inputRef,
    messages,
    inputText,
    setInputText,
    isLoading,
    chats,
    currentChatId,
    isLoadingChats,
    isInputFocused,
    setIsInputFocused,
    inputHeight,
    setInputHeight,
    previewModalData,
    setPreviewModalData,
    hoveredPreviewAction,
    setHoveredPreviewAction,
    hoveredMessageAction,
    setHoveredMessageAction,
    copiedMessageId,
    selectedChatIds,
    isDeletingChats,
    hoveredChatTitleId,
    setHoveredChatTitleId,
    copyMessageContent,
    handleReportDownload,
    handleToggleChatSelection,
    handleSelectAllChats,
    handleClearSelection,
    handleBulkDelete,
    handleSelectChat,
    handleNewChat,
    handleKeyPress,
    sendMessage,
    cancelRequest,
    usage,
    usageError,
    isCreditsExhausted,
    refreshUsage,
  }
}
